import { ComponentProps } from "react";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

import PodcastCard from "./PodcastCard";

type TopStoriesCardProps = {
  /** The article to be displayed in the card */
  cardProps: ComponentProps<typeof Card>;
  /** Unique index of the article */
  index: number;
};

/**
 * TopStoriescard component to display a articles for top stories.
 * It renders a card with the desciption.
 * https://www.figma.com/design/rR8akVWS3SksIyaOSqetu4/BAU--WEB-?node-id=13060-44324&t=5ID3HBaQRGh7o2Lq-0
 */
const TopStoriesCard = ({
  cardProps,
  index,
}: TopStoriesCardProps): React.ReactElement | null => {
  const { isScreenMD } = useWindowSize();

  const isFourthArticle = index === 3;

  const _displayAsTopTitle = !isScreenMD && !isFourthArticle;

  const variants: CardVariations = {
    content: {
      width: cn("w-[calc(100%-7.5rem)] md:w-full", {
        "w-full lg:w-1/2 xl:w-1/3": isFourthArticle,
      }),
      extraClass: cn("h-auto mt-0 md:mt-3 gap-1.5", {
        "md:mt-0 md:pr-4": isFourthArticle,
      }),
      blurb: {
        extraClass: cn(
          "font-lucida pr-4 text-base text-gray-750 max-lg:block font-medium first-letter:uppercase md:pr-0 md:hidden -tracking-5% !line-clamp-5 leading-normal",
          { "mb-3 max-lg:!hidden md:block md:text-base": isFourthArticle }
        ),
      },
    },
    image: {
      position: isFourthArticle || !isScreenMD ? "right" : "left",
      width: cn("w-[7.5rem] md:w-full", {
        "md:w-1/2 xl:w-2/3": isFourthArticle,
      }),
      orientation: "landscape",
      extraClass: cn("h-auto", {
        "display hidden lg:flex": isFourthArticle,
      }),
    },
    title: {
      size: cn("md:text-lg text-xl", {
        "text-4xl md:text-4xl": isFourthArticle,
      }),
      extraClass: cn("mb-1.5 font-lct font-semibold -tracking-2% md:mb-0", {
        "font-normal": isFourthArticle,
      }),
      color: "text-gray-815",
    },
    kicker: {
      color: "text-blue-175",
      extraClass: cn("text-base font-bold font-freightSansPro mb-1 md:mb-0"),
    },
    byline: {
      imageWidth: !isScreenMD ? 48 : 60,
      imageHeight: !isScreenMD ? 48 : 60,
      extraClass: "text-xs font-medium md:text-base",
    },
  };

  if (cardProps?.meta) {
    return (
      <div className="col-span-4 xl:col-span-3">
        <PodcastCard cardProps={{ ...cardProps, meta: undefined }} />
      </div>
    );
  }

  return (
    <Card
      {...cardProps}
      displayAsTopTitle={_displayAsTopTitle}
      variations={variants}
      className={cn(
        "relative col-span-4 flex flex-row md:flex-col",
        "relative md:before:absolute md:before:-right-4 md:before:bottom-0 md:before:top-0 md:before:w-[1px] md:before:bg-blue-100",
        "after:absolute after:-bottom-4 after:-left-0 after:-right-0 after:h-[1px] after:bg-blue-100 md:after:-left-4 md:after:-right-4",
        {
          "md:after:left-0": index === 0,
          "md:after:right-0": index === 2,
          "md:col-span-8 md:flex-row md:after:hidden xl:col-span-9":
            isFourthArticle,
          "after:hidden md:col-span-4 xl:col-span-3": index === 4,
          "md:before:hidden": index === 4 || index == 2,
        }
      )}
    />
  );
};

export default TopStoriesCard;
